import { useTranslation } from 'react-i18next'

import { Flex } from '@percent/lemonade'
import { ConfirmToggleModal } from './confirmToggleModal/ConfirmToggleModal'
import { ToggleSection } from './sections/ToggleSection'
import { BrandingSection } from './sections/BrandingSection'
import { LocalisationSection } from './sections/LocalisationSection'
import { BehaviourSection } from './sections/BehaviourSection'
import { ResetDatesSection } from './sections/ResetDatesSection'
import { usePartnerConfig } from './usePartnerConfig/usePartnerConfig'
import { usePartnerGivingConfigs } from './usePartnerGivingConfigs/usePartnerGivingConfigs'
import type { PartnerConfigProps } from './PartnerConfig.types'

const toBoolean = (value: 'enabled' | 'disabled' | boolean | undefined | null): boolean => {
  if (value === undefined || value === null) return false

  if (typeof value === 'boolean') return value

  return value === 'enabled'
}

export function PartnerConfig({ partnerId, hasWorkplaceGivingScope, hasVolunteeringScope }: PartnerConfigProps) {
  const { t } = useTranslation()
  const {
    pendingToggle,
    setPendingToggle,
    isConfirmLoading,
    confirmError,
    giving,
    donation,
    fundraising,
    donationMatching,
    handleGivingFeaturesToggle,
    handleGivingEmailsToggle,
    handleDonationGiftNoteToggle,
    handleDonationEmailsToggle,
    handleFundraisingToggle,
    handleDonationMatchingToggle,
    handleDirectToCampaignToggle
  } = usePartnerConfig(partnerId)

  const configs = usePartnerGivingConfigs(partnerId, !hasWorkplaceGivingScope, hasVolunteeringScope)

  const givingToggleRows = [
    {
      id: 'giving.features.profileSecondaryEmails',
      label: t('partnerConfig.giving.features.profileSecondaryEmails'),
      tooltip: t('partnerConfig.tooltip.profileSecondaryEmails'),
      isLoading: giving.features.isLoading,
      enabled: toBoolean(giving.features.data?.profileSecondaryEmails),
      onToggle: (val: boolean) => handleGivingFeaturesToggle('profileSecondaryEmails', val),
      error: giving.features.error
    },
    {
      id: 'giving.features.opportunityTimeLogsRewardsEligibility',
      label: t('partnerConfig.giving.features.opportunityTimeLogsRewardsEligibility'),
      tooltip: t('partnerConfig.tooltip.opportunityTimeLogsRewardsEligibility'),
      isLoading: giving.features.isLoading,
      enabled: toBoolean(giving.features.data?.opportunityTimeLogsRewardsEligibility),
      onToggle: (val: boolean) => handleGivingFeaturesToggle('opportunityTimeLogsRewardsEligibility', val),
      error: giving.features.error
    },
    {
      id: 'giving.transactionalEmails.welcomeEmail',
      label: t('partnerConfig.giving.transactionalEmails.welcomeEmail'),
      tooltip: t('partnerConfig.tooltip.welcomeEmail'),
      isLoading: giving.emails.isLoading,
      enabled: toBoolean(giving.emails.data?.welcomeEmail),
      onToggle: handleGivingEmailsToggle,
      error: giving.emails.error
    }
  ]

  const donationToggleRows = [
    {
      id: 'donation.giftNote',
      label: t('partnerConfig.donation.giftNote'),
      tooltip: t('partnerConfig.tooltip.giftNote'),
      isLoading: donation.giftNote.isLoading,
      enabled: toBoolean(donation.giftNote.data?.enabled),
      onToggle: handleDonationGiftNoteToggle,
      error: donation.giftNote.error
    },
    {
      id: 'donation.transactionalEmails.walletFundsEmail',
      label: t('partnerConfig.donation.transactionalEmails.walletFundsEmail'),
      tooltip: t('partnerConfig.tooltip.walletFundsEmail'),
      isLoading: donation.emails.isLoading,
      enabled: toBoolean(donation.emails.data?.walletFundsEmail),
      onToggle: handleDonationEmailsToggle,
      error: donation.emails.error
    }
  ]

  const fundraisingToggleRows = [
    {
      id: 'fundraising.publicFundraisers',
      label: t('partnerConfig.fundraising.publicFundraisers'),
      tooltip: t('partnerConfig.tooltip.publicFundraisers'),
      isLoading: fundraising.publicFundraisers.isLoading,
      enabled: toBoolean(fundraising.publicFundraisers.data?.enabled),
      onToggle: handleFundraisingToggle,
      error: fundraising.publicFundraisers.error
    }
  ]

  const donationMatchingToggleRows = [
    {
      id: 'donationMatching.familialDonationMatching',
      label: t('partnerConfig.donationMatching.familialDonationMatching'),
      tooltip: t('partnerConfig.tooltip.familialDonationMatching'),
      isLoading: donationMatching.familial.isLoading,
      enabled: toBoolean(donationMatching.familial.data?.enabled),
      onToggle: handleDonationMatchingToggle,
      error: donationMatching.familial.error
    },
    {
      id: 'donationMatching.directToCampaignMatchRequests',
      label: t('partnerConfig.donationMatching.directToCampaignMatchRequests'),
      tooltip: t('partnerConfig.tooltip.directToCampaignMatchRequests'),
      isLoading: donationMatching.directToCampaign.isLoading,
      enabled: toBoolean(donationMatching.directToCampaign.data?.enabled),
      onToggle: handleDirectToCampaignToggle,
      error: donationMatching.directToCampaign.error
    }
  ]

  return (
    <Flex direction="column" gap={12} pt={5} pr="xl">
      <ToggleSection title={t('partnerConfig.module.giving')} rows={givingToggleRows} />
      <ToggleSection title={t('partnerConfig.module.donation')} rows={donationToggleRows} />
      <ToggleSection title={t('partnerConfig.module.fundraising')} rows={fundraisingToggleRows} />
      <ToggleSection title={t('partnerConfig.module.donationMatching')} rows={donationMatchingToggleRows} />

      {hasWorkplaceGivingScope && (
        <>
          <BrandingSection
            theme={configs.theme}
            bannerContent={configs.bannerContent}
            isSaving={configs.isSaving}
            savingKey={configs.savingKey}
            mutationError={configs.mutationError}
            resetSaving={configs.resetSaving}
            saveTheme={configs.saveTheme}
            saveBannerContent={configs.saveBannerContent}
          />

          <LocalisationSection
            currencies={configs.currencies}
            regions={configs.regions}
            calendarIntegration={configs.calendarIntegration}
            preferredCountries={configs.preferredCountries}
            isSaving={configs.isSaving}
            savingKey={configs.savingKey}
            mutationError={configs.mutationError}
            resetSaving={configs.resetSaving}
            saveCalendarIntegration={configs.saveCalendarIntegration}
          />

          <BehaviourSection
            helpLink={configs.helpLink}
            requireOrganisationTimeLog={configs.requireOrganisationTimeLog}
            restrictedCountriesForDonation={configs.restrictedCountriesForDonation}
            spotlightedOrgs={configs.spotlightedOrgs}
            featured={configs.featured}
            isSaving={configs.isSaving}
            savingKey={configs.savingKey}
            mutationError={configs.mutationError}
            resetSaving={configs.resetSaving}
            saveHelpLink={configs.saveHelpLink}
            saveRequireOrganisationTimeLog={configs.saveRequireOrganisationTimeLog}
          />

          <ResetDatesSection
            givingDatePeriod={configs.datePeriodConfiguration}
            volunteeringAllowance={configs.volunteeringAllowance}
            achievementResetDate={configs.achievementResetDate}
            hasVolunteeringScope={hasVolunteeringScope}
          />
        </>
      )}

      {pendingToggle && (
        <ConfirmToggleModal
          isOpen
          label={pendingToggle.label}
          newEnabled={pendingToggle.newEnabled}
          isLoading={isConfirmLoading}
          errorMessage={confirmError}
          onConfirm={pendingToggle.onConfirm}
          onClose={() => setPendingToggle(null)}
        />
      )}
    </Flex>
  )
}
